import { useState, useEffect } from "react";
import { BASE_API_URL } from "../utils/constants";
import axios from "axios";
import { ServiceTypeEnum } from "../types/Building";
import { PaymentAccount } from "../types/User";
import { PaymentMethodType } from "../types/Cart";

export type FeeTypeId = 1 | 2;
export type FeeType = "FixedAmount" | "Percentage";
interface FeeData {
  feeTypeId: FeeTypeId;
  feeType: FeeType;
  value: number;
  calculatedFeeAmount: number;
  totalAmount: number;
}

type Props = {
  feeData: FeeData | null;
  error: boolean;
  loading: boolean;
};

const fetchFee = async (propertyId: number, feeData: any) => {
  const res = await axios.post(
    `${BASE_API_URL}/livly/checkout/${propertyId}/calculateServiceFee`,
    feeData
  );
  return res.data.Data;
};

export default function useServiceFee(
  propertyId: number,
  serviceType: ServiceTypeEnum,
  paymentMethod: PaymentAccount | undefined | null,
  amount: number
): Props {
  const { paymentType = "ACH", brand = "", id = 0 } = paymentMethod || {};
  const [data, setData] = useState<{
    error: boolean;
    loading: boolean;
    feeData: FeeData | null;
  }>({ error: false, loading: false, feeData: null });

  useEffect(() => {
    // Ignore responses for inputs that have since changed (e.g. the selected
    // payment account switched while a fee request was still in flight)
    let isCurrent = true;

    const doFetchFee = async () => {
      try {
        const feeData = await fetchFee(propertyId, {
          amount,
          serviceType,
          paymentType:
            paymentType === "ACH"
              ? PaymentMethodType.ACH
              : PaymentMethodType.CREDITCARD,
          paymentMethodBrand: brand,
          paymentAccountId: id,
        });

        if (isCurrent) {
          setData({ error: false, loading: false, feeData });
        }
      } catch (e: any) {
        if (isCurrent) {
          setData({ error: true, loading: false, feeData: null });
        }
      }
    };

    // Clear any fee quoted for previous inputs so it can never be submitted
    // with a different payment account or amount
    if (paymentType && brand && id && amount) {
      setData({ error: false, loading: true, feeData: null });
      doFetchFee();
    } else {
      setData({ error: false, loading: false, feeData: null });
    }

    return () => {
      isCurrent = false;
    };
  }, [propertyId, serviceType, paymentType, brand, id, amount]);

  const { error, loading, feeData } = data;

  return { feeData, loading, error };
}
